<template>
  <v-dialog v-model="dialogVisible" max-width="600" persistent>
    <v-card class="sensor-form-card">
      <v-card-title class="primary white--text">
        <v-icon dark class="mr-2">{{ isEdit ? 'edit' : 'sensors' }}</v-icon>
        {{ isEdit ? 'Edit Sensor' : 'New Sensor' }}
        <v-spacer />
        <v-btn icon dark @click="close">
          <v-icon>close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pt-6">
        <v-form ref="form" v-model="valid" lazy-validation>
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.pi_id"
                label="Sensor ID"
                prepend-icon="fingerprint"
                :rules="[rules.required]"
                :disabled="isEdit"
                hint="Unique identifier (e.g., pi-sensor-001)"
                persistent-hint
                outlined
                dense
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.client_name"
                label="Client Name"
                prepend-icon="label"
                :rules="[rules.required]"
                hint="VPN hostname (e.g., SPACE-0131AB-QQT-5)"
                persistent-hint
                outlined
                dense
              />
            </v-col>
          </v-row>

          <v-text-field
            v-model="form.description"
            label="Description"
            prepend-icon="description"
            hint="Human-readable description"
            persistent-hint
            outlined
            dense
            class="mt-3"
          />

          <v-row class="mt-2">
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.mac"
                label="MAC Address"
                prepend-icon="router"
                hint="Hardware MAC address"
                persistent-hint
                outlined
                dense
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.remark"
                label="Remark"
                prepend-icon="note"
                hint="Additional notes"
                persistent-hint
                outlined
                dense
              />
            </v-col>
          </v-row>

          <v-expansion-panels flat class="mt-4">
            <v-expansion-panel>
              <v-expansion-panel-header>
                <div>
                  <v-icon small class="mr-2">vpn_key</v-icon>
                  Credentials (Optional)
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="form.username"
                      label="Username"
                      prepend-icon="person"
                      outlined
                      dense
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="form.password"
                      label="Password"
                      prepend-icon="lock"
                      :type="showPassword ? 'text' : 'password'"
                      :append-icon="showPassword ? 'visibility' : 'visibility_off'"
                      @click:append="showPassword = !showPassword"
                      outlined
                      dense
                    />
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-form>
      </v-card-text>

      <v-divider />

      <v-card-actions class="pa-4">
        <v-spacer />
        <v-btn text @click="close">Cancel</v-btn>
        <v-btn
          color="primary"
          :loading="saving"
          :disabled="!valid"
          @click="save"
        >
          <v-icon left>save</v-icon>
          {{ isEdit ? 'Update' : 'Create' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios'

export default {
  name: 'SensorFormDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    sensor: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      valid: false,
      saving: false,
      showPassword: false,
      form: {
        pi_id: '',
        client_name: '',
        description: '',
        mac: '',
        remark: '',
        username: '',
        password: ''
      },
      rules: {
        required: v => !!v || 'This field is required'
      }
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible
      },
      set(value) {
        if (!value)
          this.$emit('close')
      }
    },
    isEdit() {
      return !!this.sensor
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.resetForm()
        if (this.sensor)
          this.form = {
            pi_id: this.sensor.pi_id || '',
            client_name: this.sensor.client_name || '',
            description: this.sensor.description || '',
            mac: this.sensor.mac || '',
            remark: this.sensor.remark || '',
            username: this.sensor.username || '',
            password: this.sensor.password || ''
          }
      }
    }
  },
  methods: {
    resetForm() {
      this.form = {
        pi_id: '',
        client_name: '',
        description: '',
        mac: '',
        remark: '',
        username: '',
        password: ''
      }
      this.showPassword = false
      if (this.$refs.form)
        this.$refs.form.resetValidation()
    },
    close() {
      this.$emit('close')
    },
    async save() {
      if (!this.$refs.form.validate())
        return

      this.saving = true
      try {
        if (this.isEdit) {
          // eslint-disable-next-line camelcase
          const {pi_id: piId, ...updateData} = this.form
          await axios.put(`/api/sensors/${piId}`, updateData)
        } else
          await axios.post('/api/sensors', this.form)

        this.$emit('saved')
        this.close()
      } catch (error) {
        console.error('Failed to save sensor:', error)
        const message = (error.response && error.response.data && error.response.data.error) || 'Failed to save sensor'
        // eslint-disable-next-line no-alert
        window.alert(message)
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.sensor-form-card {
  border-radius: 12px !important;
}
</style>
